import React, { useRef, useEffect } from 'react';
import { AlertCircle, Lock, XCircle, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

// 🆕 Global singleton to ensure banner only renders once per session
let bannerShownThisSession = false;

/**
 * PaymentStatusBanner Component
 * Displays warning banners based on subscription billing status
 * 
 * ✅ DEDUPE: Only renders ONCE per page, even if component mounts multiple times
 * 
 * Status levels:
 * - past_due: Yellow warning (0-3 days) - Grace period
 * - unpaid: Orange warning (4-10 days) - Read-only mode
 * - suspended: Red blocking overlay (11+ days) - Account suspended
 */
export const PaymentStatusBanner = ({ billingStatus, paymentFailedAt }) => {
  const navigate = useNavigate();
  const hasRendered = useRef(false);

  // 🆕 DEDUPE LOGIC: Prevent multiple renders (MUST BE BEFORE ANY RETURNS!)
  useEffect(() => {
    if (!hasRendered.current) {
      hasRendered.current = true;
      bannerShownThisSession = true;
    }
  }, []);

  // Don't show banner for active subscriptions
  if (!billingStatus || billingStatus === 'active' || billingStatus === 'trialing') {
    return null;
  }

  // Calculate days since payment failed
  const getDaysFailed = () => {
    if (!paymentFailedAt) return 0;
    const failedDate = new Date(paymentFailedAt);
    const now = new Date();
    return Math.floor((now - failedDate) / (1000 * 60 * 60 * 24));
  };

  const daysFailed = getDaysFailed();

  // LOCKED (Tag 5-6): Vollständige Sperrung
  if (billingStatus === 'locked') {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 border-2 border-orange-500">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
              <Lock className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-orange-900 dark:text-orange-100">Konto vorübergehend gesperrt</h2>
              <p className="text-sm text-orange-700 dark:text-orange-300">{daysFailed > 0 && `${daysFailed} Tage überfällig`}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">Ihre Zahlung ist seit 5 Tagen ausstehend.</p>
              <p className="text-sm text-orange-800 dark:text-orange-200">Bitte aktualisieren Sie Ihre Zahlungsmethode – alle Daten bleiben erhalten.</p>
              <p className="text-xs text-orange-600 dark:text-orange-400">Hinweis: Ab Tag 7 sind nur noch Daten-Exports möglich.</p>
            </div>
            <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white" onClick={() => navigate('/admin/subscription')}>
              <CreditCard className="h-4 w-4 mr-2" /> Jetzt Zahlung aktualisieren
            </Button>
            <p className="text-xs text-center text-gray-500">Fragen? <a href="mailto:support@allflowx.de" className="text-blue-600 hover:underline">support@allflowx.de</a></p>
          </div>
        </div>
      </div>
    );
  }

  // LOCKED_EXPORTS (Tag 7+) oder cancelled: Nur noch Exports
  // WICHTIG: Bei regulärer/manueller Kündigung (kein paymentFailedAt) → Modal NICHT zeigen.
  // OnboardingContext leitet diese Kunden bereits korrekt zum Exporte-Modul.
  // Das Modal erscheint nur bei echtem Zahlungsausfall (paymentFailedAt gesetzt).
  if (billingStatus === 'locked_exports' || (billingStatus === 'cancelled' && paymentFailedAt)) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 border-2 border-red-500">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900 dark:text-red-100">
                {billingStatus === 'cancelled' ? 'Abonnement gekündigt' : 'Konto gesperrt – nur Exports'}
              </h2>
              <p className="text-sm text-red-700 dark:text-red-300">{daysFailed > 0 && `${daysFailed} Tage überfällig`}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                {billingStatus === 'cancelled' ? 'Ihr Abonnement wurde nach nicht erfolgter Zahlung gekündigt.' : 'Zahlung seit über 7 Tagen ausstehend.'}
              </p>
              <p className="text-sm text-red-800 dark:text-red-200">Alle Ihre Daten sind sicher. Nach der Zahlung erhalten Sie sofort wieder vollen Zugriff.</p>
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => navigate('/admin/subscription')}>
                <CreditCard className="h-4 w-4 mr-2" /> Jetzt bezahlen
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => navigate('/admin/exports-overview')}>
                Daten exportieren
              </Button>
            </div>
            <p className="text-xs text-center text-gray-500">Fragen? <a href="mailto:support@allflowx.de" className="text-blue-600 hover:underline">support@allflowx.de</a></p>
          </div>
        </div>
      </div>
    );
  }

  // PAST_DUE: Yellow warning banner (Grace period)
  if (billingStatus === 'past_due') {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b-2 border-yellow-400 dark:border-yellow-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">
                  ⚠️ Zahlung fehlgeschlagen
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  Ihre letzte Zahlung konnte nicht durchgeführt werden. Bitte aktualisieren Sie Ihre Zahlungsmethode.
                  {daysFailed > 0 && ` (${daysFailed} ${daysFailed === 1 ? 'Tag' : 'Tage'} überfällig)`}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/admin/subscription')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Zahlungsmethode aktualisieren
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // UNPAID: Orange warning banner (Read-only mode)
  if (billingStatus === 'unpaid') {
    return (
      <div className="bg-orange-50 dark:bg-orange-900/20 border-b-2 border-orange-500 dark:border-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                  🔒 Eingeschränkter Zugriff - Zahlung überfällig
                </p>
                <p className="text-xs text-orange-800 dark:text-orange-200">
                  Ihr Konto ist im Read-Only-Modus. Sie können keine Änderungen vornehmen, bis die Zahlung erfolgt ist.
                  {daysFailed > 0 && ` (${daysFailed} ${daysFailed === 1 ? 'Tag' : 'Tage'} überfällig)`}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/admin/subscription')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Jetzt bezahlen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // SUSPENDED: Red blocking overlay (Account suspended)
  if (billingStatus === 'suspended') {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-md w-full p-6 border-2 border-red-500">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900 dark:text-red-100">
                Account gesperrt
              </h2>
              <p className="text-sm text-red-700 dark:text-red-300">
                {daysFailed > 0 && `${daysFailed} ${daysFailed === 1 ? 'Tag' : 'Tage'} überfällig`}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-900 dark:text-red-100 mb-2">
                <strong>Ihr Konto wurde gesperrt</strong>
              </p>
              <p className="text-xs text-red-800 dark:text-red-200">
                Ihre Zahlung ist seit mehr als 10 Tagen überfällig. Um Ihren Zugriff wiederherzustellen, 
                aktualisieren Sie bitte Ihre Zahlungsmethode oder kontaktieren Sie uns.
              </p>
            </div>

            <div className="space-y-2">
              <Button
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                onClick={() => navigate('/admin/subscription')}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Zahlungsmethode aktualisieren
              </Button>
              
              <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                Benötigen Sie Hilfe? Kontaktieren Sie unseren Support unter{' '}
                <a href="mailto:support@allflowx.com" className="text-blue-600 hover:underline">
                  support@allflowx.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
